import React, { useMemo } from 'react';

const mono = { fontFamily: 'JetBrains Mono, monospace' };
const SEV_COLORS = { CRITICAL: '#ff3b4e', HIGH: '#f97316', MEDIUM: '#f59e0b', LOW: '#10b981' };

export default function RelatedAlerts({ alert, allAlerts = [], onClose }) {
  
  const srcIp = alert ? (alert.src_ip || alert.source_ip) : null;

  const { sameIP, sharedTargets } = useMemo(() => {
    if (!alert) return { sameIP: [], sharedTargets: [] };

    const sameIP = allAlerts
      .filter(a => {
        const aIp = a.src_ip || a.source_ip;
        return aIp === srcIp && a !== alert;
      })
      .slice(0, 8);

    const typeMap = {};
    allAlerts.forEach(a => {
      if (!a.attack_type || a.attack_type === alert.attack_type) return;
      const ip = a.src_ip || a.source_ip;
      if (!typeMap[a.attack_type]) typeMap[a.attack_type] = new Set();
      typeMap[a.attack_type].add(ip);
    });

    const sharedTargets = Object.entries(typeMap)
      .filter(([, ips]) => ips.size >= 2)
      .sort((a, b) => b[1].size - a[1].size)
      .slice(0, 4)
      .map(([type, ips]) => ({ type, count: ips.size }));

    return { sameIP, sharedTargets };
  }, [alert, allAlerts, srcIp]);

  
  if (!alert) return null;

  const sev = (alert.severity || alert.threat_level || 'LOW').toUpperCase();
  const col = SEV_COLORS[sev] || '#6b7280';

  return (
    <div style={{ background: '#141720', border: `1px solid ${col}33`, borderRadius: 8, padding: '14px 16px', marginTop: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <span style={{ fontSize: 11, color: '#9ca3af', ...mono, letterSpacing: '.1em' }}>RELATED ALERTS</span>
          <span style={{ fontSize: 10, color: '#4b5563', ...mono, marginLeft: 10 }}>Source: {srcIp}</span>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 14, padding: '0 4px' }}>✕</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <div style={{ fontSize: 10, color: '#4b5563', ...mono, marginBottom: 8 }}>
            SAME SOURCE IP ({sameIP.length} other alerts)
          </div>
          {sameIP.length === 0 ? (
            <div style={{ fontSize: 11, color: '#374151', ...mono }}>No other alerts from this IP</div>
          ) : sameIP.map((a, i) => {
            const aSev = (a.severity || a.threat_level || 'LOW').toUpperCase();
            const aCol = SEV_COLORS[aSev] || '#6b7280';
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', borderBottom: '1px solid #ffffff0a' }}>
                <div style={{ width: 5, height: 5, borderRadius: '50%', background: aCol, flexShrink: 0 }} />
                <span style={{ fontSize: 10, color: '#9ca3af', ...mono, flex: 1 }}>{a.attack_type}</span>
                <span style={{ fontSize: 9, color: aCol, ...mono, background: `${aCol}18`, padding: '1px 5px', borderRadius: 3 }}>{aSev}</span>
              </div>
            );
          })}
        </div>

        <div>
          <div style={{ fontSize: 10, color: '#4b5563', ...mono, marginBottom: 8 }}>
            COORDINATED PATTERNS (multi-source)
          </div>
          {sharedTargets.length === 0 ? (
            <div style={{ fontSize: 11, color: '#374151', ...mono }}>No coordinated patterns detected</div>
          ) : sharedTargets.map(({ type, count }, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #ffffff0a' }}>
              <span style={{ fontSize: 10, color: '#9ca3af', ...mono }}>{type.replace('APT-', '')}</span>
              <span style={{ fontSize: 10, color: '#60a5fa', ...mono }}>{count} IPs</span>
            </div>
          ))}
          <div style={{ marginTop: 10, padding: '6px 8px', background: '#0d1018', borderRadius: 4, fontSize: 10, color: '#4b5563', ...mono }}>
            {sameIP.length > 2 ? '⚠ High activity from this source — consider isolation' : 'Activity within normal range'}
          </div>
        </div>
      </div>
    </div>
  );
}
